import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from '@mui/material';
import { PresentationSlide } from '../types';
import { PRES } from '../chrome';
import { visibleSlides } from './slideOrder';
import { COPY } from '../presentationCopy';

interface PdfExportDialogProps {
  open: boolean;
  slides: PresentationSlide[];
  activeSlideId?: string | null;
  busy?: boolean;
  onClose: () => void;
  onExport: (slideIds: string[]) => void;
}

const PdfExportDialog: React.FC<PdfExportDialogProps> = ({
  open, slides, activeSlideId, busy, onClose, onExport,
}) => {
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (open) setSelected(visibleSlides(slides).map((slide) => slide.slideId));
  }, [open, slides]);

  const toggle = (slideId: string) => {
    setSelected((current) => (
      current.includes(slideId) ? current.filter((id) => id !== slideId) : [...current, slideId]
    ));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: PRES.textPrimary, fontWeight: 700 }}>Exportera PDF</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: PRES.muted, mb: 1.5 }}>
          Välj vilka sidor som ska ingå i PDF:en.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" onClick={() => setSelected(slides.map((slide) => slide.slideId))} sx={{ textTransform: 'none' }}>
            Alla
          </Button>
          <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none' }}>
            Inga
          </Button>
          {activeSlideId && (
            <Button size="small" onClick={() => setSelected([activeSlideId])} sx={{ textTransform: 'none' }}>
              Denna slide
            </Button>
          )}
        </Box>
        <Box sx={{ maxHeight: 360, overflow: 'auto', border: `1px solid ${PRES.line}`, borderRadius: 1, px: 1 }}>
          {slides.map((slide, index) => (
            <FormControlLabel
              key={slide.slideId}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(slide.slideId)}
                  onChange={() => toggle(slide.slideId)}
                  sx={{ color: PRES.textSecondary, '&.Mui-checked': { color: PRES.interactive } }}
                />
              }
              label={`Sida ${index + 1}${slide.hidden ? ` (${COPY.slideHidden})` : ''}${slide.sectionTitle ? ` — ${slide.sectionTitle}` : ''}`}
              sx={{ display: 'flex', color: PRES.textPrimary }}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Avbryt</Button>
        <Button
          variant="contained"
          disabled={busy || selected.length === 0}
          onClick={() => onExport(selected)}
          sx={{ textTransform: 'none', bgcolor: PRES.interactive, boxShadow: 'none', '&:hover': { bgcolor: PRES.interactiveHover } }}
        >
          {busy ? 'Exporterar…' : `Exportera ${selected.length} ${selected.length === 1 ? 'sida' : 'sidor'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PdfExportDialog;
